import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { PalmettoFileUpload, PalmettoFileUploadProps, UseFileUploadReturn } from '@palmetto/dsm-components';
import React, { useCallback, useState } from 'react';

import { useTranslation } from '../../providers/TranslationProvider/TranslationProvider';
import { Button } from '../Button/Button';
import { Card } from '../Card/Card';
import { InfoPopover } from '../InfoPopover/InfoPopover';
import { VideoModal } from '../VideoModal/VideoModal';
import { Cta } from './cta';

export const DEFAULT_ACCEPTED_FILE_TYPES = {
  'application/pdf': ['.pdf'],
  'image/*': ['.jpg', '.jpeg', '.png', '.bmp', '.webp'],
};

export interface FileUploadCardProps {
  title: string;
  description: string;
  infoPopover?: { label: string; content: string };
  taskVideoLink?: { label: string; videoUrls: { desktop: string; mobile: string }; trackingId: string };
  api: UseFileUploadReturn;
  primaryAction: Cta;
  secondaryAction?: Cta;
  errorMessage?: string;
  className?: string;
  maxFileSize?: number;
}

const isNativePlatform = Capacitor.isNativePlatform();

interface NativePromptLabels {
  promptLabelHeader: string;
  promptLabelCancel: string;
  promptLabelPhoto: string;
  promptLabelPicture: string;
}

const captureNativeFile = async (promptLabels: NativePromptLabels): Promise<File | null> => {
  const image = await Camera.getPhoto({
    quality: 90,
    allowEditing: false,
    resultType: CameraResultType.Uri,
    source: CameraSource.Prompt,
    ...promptLabels,
  });

  if (!image.webPath) return null;

  const response = await fetch(image.webPath);
  const blob = await response.blob();
  return new File([blob], image.path?.split('/').pop() ?? 'photo.jpg', {
    type: blob.type || 'image/jpeg',
  });
};

const FileUploadCard: React.FC<FileUploadCardProps> = ({
  title,
  description,
  infoPopover,
  taskVideoLink,
  api,
  primaryAction,
  secondaryAction,
  errorMessage,
  className,
  maxFileSize,
}) => {
  const { t } = useTranslation();
  const localePrefix = 'fileUploadCard';
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  const { label: primaryLabel, children: primaryChildren, ...primaryButtonProps } = primaryAction;
  const primaryContent = (primaryChildren ?? primaryLabel) as string;
  const removeFileAriaLabel = (fileName: string) => t(`${localePrefix}.removeFileAriaLabel`, { fileName });
  const dismissFileAriaLabel = (fileName: string) => t(`${localePrefix}.dismissFileAriaLabel`, { fileName });

  const formattedMaxSize = maxFileSize ? `${Math.round(maxFileSize / (1024 * 1024))} MB` : undefined;

  const defaultFileUploadProps: Partial<PalmettoFileUploadProps> = {
    dropzoneText: t(`${localePrefix}.dropzoneText`),
    dropzoneSubText: t(`${localePrefix}.dropzoneSubText`),
    fileErrorMessages: {
      FILE_INVALID_TYPE: t(`${localePrefix}.fileErrorMessages.FILE_INVALID_TYPE`),
      FILE_TOO_LARGE: formattedMaxSize
        ? t(`${localePrefix}.fileErrorMessages.FILE_TOO_LARGE_WITH_LIMIT`, { maxFileSize: formattedMaxSize })
        : t(`${localePrefix}.fileErrorMessages.FILE_TOO_LARGE`),
      FILE_TOO_SMALL: t(`${localePrefix}.fileErrorMessages.FILE_TOO_SMALL`),
      TOO_MANY_FILES: t(`${localePrefix}.fileErrorMessages.TOO_MANY_FILES`),
      FILE_EXISTS: t(`${localePrefix}.fileErrorMessages.FILE_EXISTS`),
    },
  };

  const promptLabels: NativePromptLabels = {
    promptLabelHeader: t(`${localePrefix}.promptLabelHeader`),
    promptLabelCancel: t(`${localePrefix}.promptLabelCancel`),
    promptLabelPhoto: t(`${localePrefix}.promptLabelPhoto`),
    promptLabelPicture: t(`${localePrefix}.promptLabelPicture`),
  };

  const handleNativeCapture = useCallback(
    async (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;

      if (
        target.closest('[data-part="item"]') ||
        target.closest('[data-part="item-delete-trigger"]') ||
        target.closest('[data-part="clear-trigger"]')
      ) {
        return;
      }

      if (!target.closest('[data-part="trigger"]') && !target.closest('[data-part="dropzone"]')) {
        return;
      }

      e.stopPropagation();
      e.preventDefault();
      const file = await captureNativeFile(promptLabels);
      if (file) {
        api.setFiles([...api.acceptedFiles, file]);
      }
    },
    [api, promptLabels],
  );

  return (
    <Card className={`p-28 w-full max-w-lg ${className ?? ''}`}>
      <div className="flex flex-col gap-28 w-full">
        <div className="flex flex-col gap-10 w-full">
          <h2 className="label-400 text-text-primary m-0">{title}</h2>
          <p className="body-300 text-text-secondary m-0 whitespace-pre-line">{description}</p>
        </div>

        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div onClickCapture={isNativePlatform ? handleNativeCapture : undefined}>
          <PalmettoFileUpload
            {...defaultFileUploadProps}
            api={api}
            variant="dropzone"
            removeFileAriaLabel={removeFileAriaLabel}
            dismissFileAriaLabel={dismissFileAriaLabel}
            validationMessage={errorMessage ? { text: errorMessage, type: 'error' } : undefined}
          />
        </div>
      </div>

      <div className="flex flex-col gap-16 items-center w-full pt-48">
        {infoPopover && (
          <div className="flex gap-1.5 items-center">
            <span className="body-300 text-text-tertiary whitespace-nowrap">{infoPopover.label}</span>
            <InfoPopover content={infoPopover.content} />
          </div>
        )}

        {taskVideoLink && (
          <>
            <Button
              type="button"
              emphasis="text"
              onClick={() => setIsVideoOpen(true)}
              size="medium"
              trackingId={`${taskVideoLink.trackingId}-why-button`}
            >
              {taskVideoLink.label}
            </Button>
            <VideoModal
              isOpen={isVideoOpen}
              onClose={() => setIsVideoOpen(false)}
              videoUrls={taskVideoLink.videoUrls}
              trackingId={taskVideoLink.trackingId}
            />
          </>
        )}

        <div className="flex flex-col gap-8 w-full">
          <Button layout="fill" {...primaryButtonProps}>
            {primaryContent}
          </Button>

          {secondaryAction && <SecondaryButton action={secondaryAction} />}
        </div>
      </div>
    </Card>
  );
};

const SecondaryButton: React.FC<{ action: Cta }> = ({ action }) => {
  const { label, children, ...buttonProps } = action;
  return (
    <Button layout="fill" emphasis="secondary" {...buttonProps}>
      {(children ?? label) as string}
    </Button>
  );
};

export default FileUploadCard;
